import { format, isValid, type Locale, parse } from 'date-fns';
import {
    bg,
    cs,
    da,
    de,
    el,
    enGB,
    es,
    et,
    fi,
    fr,
    hr,
    hu,
    it,
    ko,
    lt,
    lv,
    nb,
    nl,
    pl,
    pt,
    ptBR,
    ro,
    sk,
    sl,
    sv,
} from 'date-fns/locale';
import { formatInTimeZone } from 'date-fns-tz';
import { getSupportedLocale, SupportedLocale } from '../../../configuration/lang/lang';

const TIME_FORMAT = 'HH:mm';

const dateFnsLocaleMap: Record<SupportedLocale, Locale> = {
    [SupportedLocale.bgBG]: bg,
    [SupportedLocale.csCZ]: cs,
    [SupportedLocale.daDK]: da,
    [SupportedLocale.deDE]: de,
    [SupportedLocale.elGR]: el,
    [SupportedLocale.enGB]: enGB,
    [SupportedLocale.esES]: es,
    [SupportedLocale.etEE]: et,
    [SupportedLocale.fiFI]: fi,
    [SupportedLocale.frFR]: fr,
    [SupportedLocale.hrHR]: hr,
    [SupportedLocale.huHU]: hu,
    [SupportedLocale.itIT]: it,
    [SupportedLocale.koKR]: ko,
    [SupportedLocale.ltLT]: lt,
    [SupportedLocale.lvLV]: lv,
    [SupportedLocale.nbNO]: nb,
    [SupportedLocale.nlNL]: nl,
    [SupportedLocale.plPL]: pl,
    [SupportedLocale.ptBR]: ptBR,
    [SupportedLocale.ptPT]: pt,
    [SupportedLocale.roRO]: ro,
    [SupportedLocale.skSK]: sk,
    [SupportedLocale.slSI]: sl,
    [SupportedLocale.svSE]: sv,
};

const getDateFnsLocale = (locale: string): Locale => dateFnsLocaleMap[getSupportedLocale(locale)] ?? enGB;

const getLocalizedDateTimeFormat = (dateFnsLocale: Locale): string => {
    return `${dateFnsLocale.formatLong.date({ width: 'short' })} ${TIME_FORMAT}`;
};

export const parseLocalizedDateTime = (rawValue: string, locale: string): Date | undefined => {
    const dateFnsLocale = getDateFnsLocale(locale);
    const dateTimeFormat = getLocalizedDateTimeFormat(dateFnsLocale);
    const parsed = parse(rawValue, dateTimeFormat, new Date(), { locale: dateFnsLocale });

    if (!isValid(parsed)) {
        return undefined;
    }

    // we only accept date strings in the format YYYY*MM*DD HH:MM skipping a leading 0 in month or day will result
    // in a valid date, but does not conform to the downstream expected date format,
    // therefore we only return a valid date, if its string value matches the raw string value
    return format(parsed, dateTimeFormat, { locale: dateFnsLocale }) === rawValue ? parsed : undefined;
};

export const formatLocalizedDateTimeInTimeZone = (dateTime: Date, timeZone: string, locale: string): string => {
    const dateFnsLocale = getDateFnsLocale(locale);

    return formatInTimeZone(dateTime, timeZone, getLocalizedDateTimeFormat(dateFnsLocale), { locale: dateFnsLocale });
};
